import type { MapNode } from '../lib/types';
import { nodeTint, ROOT_GRADIENT } from '../lib/color';

/**
 * A node drawn the way the map draws it — its colour as a swatch and a tinted border around
 * the label — minus the doc bubbles and tag pills. THE ONE IMPLEMENTATION (V0.44): the AI
 * console's table has rendered this since the port, and the dashboard's Documents tab renders
 * the same chip, so the two cannot drift a shade apart.
 *
 * A `<span>` by default (what the console always was, and what its baseline photographs); with
 * `onClick` it becomes a `<button>` carrying the same classes, so a table can make the chip the
 * way to the node without a second, subtly different chip beside it.
 *
 * THE POPUP'S SEAT IS THIS CHIP TOO (V0.47), in its `seat` size. When a popup opens, the node
 * on the map lifts off and flies into the top of the popup; what it lands on has to be a node
 * drawn the map's way, or there is nothing for it to become — and it had better be the chip
 * that already exists rather than a fifth copy of the same three colours. Three props exist
 * for that one consumer: `labelId` because the regression suite reads the title as
 * `#pp-title`, `dataId` because the flight finds its destination by node id, and `empty`
 * because the seat is drawn before the node arrives in it. The paint itself comes from
 * `nodeTint`, which every other node-shaped box also uses.
 *
 * AND THE SEAT IS WHERE THE NAME IS EDITED (V0.51). The popup used to carry a "Name" line and a
 * "Color" line under a seat that showed the same name as plain text — the node twice, once as
 * itself and once as a form field. `nameEditor` and `swatchInput` fold those two lines into the
 * chip: the label becomes an input lying over `#pp-title`, and the 11px swatch becomes the
 * colour picker. Both are OPTIONAL and both absent is today's markup byte for byte, which is
 * what keeps the console's table, the Documents tab and the READ popup's seat untouched.
 *
 * `#pp-title` STAYS A TEXT ELEMENT and keeps showing the label — the draft's, while somebody is
 * typing. Seven specs read it with `toHaveText`, and `.seat-name` is a one-cell grid precisely
 * so the span can stay in the DOM sizing the input rather than being replaced by it.
 */

/** The name, edited in the chip (V0.51). Absent → the label is plain text, as it always was. */
export interface ChipNameEditor {
  value: string;
  onChange: (v: string) => void;
  /** Escape: put back what was there when the field took focus. */
  onCancel: () => void;
  /** The field took focus — the caller's cue to record what to put back. */
  onFocus?: () => void;
}

/* THERE IS NO `onCommit`, DELIBERATELY. Blur is not a commit here: the draft is already current
   on every keystroke, and Save is what writes. An onBlur that tidied the value — trimming an
   emptied name back to the old one, say — would be a SECOND place deciding what an empty name
   means, and `savePopup` already owns that rule (`(label || '').trim() || 'Untitled'`). The
   suite caught exactly that: `36-popup`'s "an emptied name saves as Untitled" fills the field
   with spaces and clicks Save, and a tidying blur silently put the old name back. */

/** The colour, picked from the swatch itself (V0.51). Absent → the swatch is a plain dot. */
export interface ChipSwatchInput {
  value: string;
  onChange: (v: string) => void;
}

/**
 * The ✎ in the corner of an editable seat.
 *
 * ALWAYS DRAWN, not on hover — the user's choice from the proof of concept. A rename affordance
 * that appears only under the pointer is one nobody finds who did not already know it was
 * there, and the chip is the one place in the popup where "this is editable" is not obvious
 * from the shape of the control.
 */
function PenMark() {
  return (
    <svg className="pen" aria-hidden="true">
      <use href="#i-edit" />
    </svg>
  );
}

export function NodeChip({
  node,
  isLight,
  onClick,
  title,
  variant,
  labelId,
  dataId,
  empty,
  nameEditor,
  swatchInput,
  pen,
}: {
  node: Pick<MapNode, 'root' | 'color' | 'label'>;
  isLight: boolean;
  onClick?: () => void;
  title?: string;
  /** `seat`: the popup header's size, between a map node and the band's `big` bubble. */
  variant?: 'seat';
  /** An id for the label span — the popup's `#pp-title`, which the suite reads by name. */
  labelId?: string;
  /** Stamped as `data-id`, so a flight can find this box as an anchor. */
  dataId?: string;
  /** The node is not here yet (or has left): the box stays, its contents go transparent. */
  empty?: boolean;
  /** Makes the label editable in place, as `#pp-name`. The popup's editable seat only. */
  nameEditor?: ChipNameEditor;
  /** Makes the swatch the colour picker, as `#pp-color`. Never for a root — it has a gradient. */
  swatchInput?: ChipSwatchInput;
  /** Draw the ✎ that says the name can be edited here. */
  pen?: boolean;
}) {
  const interactive = onClick ? { font: 'inherit', cursor: 'pointer' } : {};
  const classes = ['aidoc-node'];
  if (node.root) classes.push('root');
  if (variant) classes.push(variant);
  if (empty) classes.push('empty');
  const className = classes.join(' ');
  const data = dataId ? { 'data-id': dataId } : {};

  /* The label, or the label with an input lying over it. ONE CELL of a grid, so the span keeps
     sizing the box while the input takes the typing — an input sized independently would make
     the chip jump the moment it appeared, and the flight's destination is re-measured every
     frame from exactly this box. */
  const labelNode = nameEditor ? (
    <span className="seat-name">
      <span className="txt" id={labelId}>
        {nameEditor.value || ' '}
      </span>
      <input
        id="pp-name"
        className="seat-in"
        aria-label="Node name"
        value={nameEditor.value}
        onChange={(e) => nameEditor.onChange(e.target.value)}
        onFocus={nameEditor.onFocus}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            /* Not the popup's Escape: this one puts the name back. Stopped, or `chrome.ts`
               would close the popup out from under somebody abandoning a rename. */
            e.stopPropagation();
            nameEditor.onCancel();
            e.currentTarget.blur();
          }
        }}
      />
    </span>
  ) : (
    <span className="txt" id={labelId}>
      {node.label || ''}
    </span>
  );

  if (node.root) {
    const style = { background: ROOT_GRADIENT, ...interactive };
    const inner = (
      <>
        {labelNode}
        {pen ? <PenMark /> : null}
      </>
    );
    return onClick ? (
      <button type="button" className={className} style={style} onClick={onClick} title={title} {...data}>
        {inner}
      </button>
    ) : (
      <span className={className} style={style} title={title} {...data}>
        {inner}
      </span>
    );
  }
  const c = node.color || '#5b8cff';
  const style = { ...nodeTint(c, isLight), ...interactive };
  const inner = (
    <>
      {swatchInput ? (
        /* The dot IS the picker — `.tagrow input.dot`'s trick (app.css), an `appearance:none`
           colour input at the swatch's own 11px. The colour line it replaces was a 56×32 box
           under a heading, for a value the chip was already showing. */
        <input
          type="color"
          id="pp-color"
          className="swatch"
          aria-label="Node colour"
          title="Node colour"
          value={swatchInput.value}
          onChange={(e) => swatchInput.onChange(e.target.value)}
        />
      ) : (
        <span className="swatch" style={{ background: c }} />
      )}
      {labelNode}
      {pen ? <PenMark /> : null}
    </>
  );
  return onClick ? (
    <button type="button" className={className} style={style} onClick={onClick} title={title} {...data}>
      {inner}
    </button>
  ) : (
    <span className={className} style={style} title={title} {...data}>
      {inner}
    </span>
  );
}
